import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  Button,
  Spinner,
  Banner,
} from '@shopify/polaris';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  productCount: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function CategoriesPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCategories() {
      try {
        const res = await fetch(`${API_URL}/categories`);
        if (!res.ok) throw new Error('Failed to fetch categories');
        const data = await res.json();
        setCategories(data.data || []);
      } catch (err) {
        setError('Failed to load categories.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <Page title="Categories">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading categories...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  return (
    <Page title="All Categories" subtitle="Browse our product categories">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 3 }} gap="400">
            {categories.map((category) => (
              <Card key={category.id}>
                <BlockStack gap="300">
                  {category.bannerUrl && (
                    <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
                      <img
                        src={category.bannerUrl}
                        alt={category.name}
                        style={{
                          width: '100%',
                          height: '150px',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  )}
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingMd">
                      {category.name}
                    </Text>
                    {category.description && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {category.description.substring(0, 100)}
                        {category.description.length > 100 ? '...' : ''}
                      </Text>
                    )}
                    <Text as="p" variant="bodySm">
                      {category.productCount} products
                    </Text>
                    <Button onClick={() => navigate(`/categories/${category.slug}`)}>
                      View Products
                    </Button>
                  </BlockStack>
                </BlockStack>
              </Card>
            ))}
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
